// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import UserProfileSetup from './components/UserProfileSetup';
import ConsentGate from './components/ConsentGate';
import HIPAAGate from './components/HIPAAGate';
import Login from './pages/Login';
import EmailVerification from './pages/EmailVerification';
import Clients from './pages/Clients';
import Associates from './pages/Associates';
import Billing from './pages/Billing';
import Calendar from './pages/Calendar';
import ClinicalForms from './pages/ClinicalForms';
import FormTemplates from './pages/FormTemplates';
import AdminPanel from './pages/AdminPanel';
import AISummary from './pages/AISummary';
import Home from './pages/Home';
import MyClients from './pages/MyClients';
import TherapyNotes from './pages/TherapyNotes';
import ClientInterface from './pages/ClientInterface';
import ClientIntakeForm from './pages/ClientIntakeForm';
import IntakePreview from './pages/IntakePreview';
import SelfAssessment from './pages/SelfAssessment';
import ClientSignUp from './pages/ClientSignUp';
import ForgotPassword from './pages/ForgotPassword';
import StaffSignUp from './pages/StaffSignUp';
import InformedConsent from './pages/InformedConsent';
import './App.css';

// Separate layout for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 bg-white">
        {children}
      </main>
    </div>
  );
};

// Separate layout for unauthenticated pages (like login)
const UnauthenticatedLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-100">
      {children}
    </div>
  );
};

// Component to handle profile setup flow
const ProfileSetupWrapper = ({ children }) => {
  const { currentUser, needsProfileSetup, setNeedsProfileSetup } = useAuth();

  if (needsProfileSetup && currentUser) {
    return (
      <UserProfileSetup
        onComplete={() => {
          setNeedsProfileSetup(false);
        }}
      />
    );
  }

  return children;
};

// Gate for client accounts that haven't acknowledged informed consent yet.
// Shows once on first login after the requirement was introduced, then never again.
const ConsentGateWrapper = ({ children }) => {
  const { currentUser } = useAuth();

  if (currentUser?.role === 'client' && !currentUser?.consentAcknowledgedAt) {
    return <ConsentGate />;
  }

  return children;
};

// Gate for staff accounts that haven't acknowledged the HIPAA workforce agreement yet.
// Shows once on first login after the requirement was introduced, then never again.
const STAFF_ROLES = ['admin', 'therapist', 'billing'];
const HIPAAGateWrapper = ({ children }) => {
  const { currentUser } = useAuth();

  if (currentUser && STAFF_ROLES.includes(currentUser.role) && !currentUser.hipaaAcknowledgedAt) {
    return <HIPAAGate />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProfileSetupWrapper>
          <HIPAAGateWrapper>
          <ConsentGateWrapper>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/client-signup" element={
              <UnauthenticatedLayout>
                <ClientSignUp />
              </UnauthenticatedLayout>
            } />
            <Route path="/login" element={
              <UnauthenticatedLayout>
                <Login />
              </UnauthenticatedLayout>
            } />
            <Route path="/staff-signup" element={
              <UnauthenticatedLayout>
                <StaffSignUp />
              </UnauthenticatedLayout>
            } />
            <Route path="/verify-email" element={
              <UnauthenticatedLayout>
                <EmailVerification />
              </UnauthenticatedLayout>
            } />
            <Route path="/forgot-password" element={
              <UnauthenticatedLayout>
                <ForgotPassword />
              </UnauthenticatedLayout>
            } />

            {/* Protected routes */}
            <Route path="/clients" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Clients />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/associates" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Associates />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Billing />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Calendar />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/forms" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ClinicalForms />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/forms/ai-summary" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <AISummary />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <AdminPanel />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/clinical-forms" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <FormTemplates />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/my-clients" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyClients />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/therapy-notes" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <TherapyNotes />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/client-interface" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ClientInterface />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/intake-preview" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <IntakePreview />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/intake" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ClientIntakeForm />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/self-assessment" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <SelfAssessment />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            <Route path="/informed-consent" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <InformedConsent />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
          </Routes>
          </ConsentGateWrapper>
          </HIPAAGateWrapper>
        </ProfileSetupWrapper>
      </AuthProvider>
    </Router>
  );
}

export default App;
